import { useEffect, useState, type JSX } from 'react'
import { Link, useNavigate } from 'react-router'
import {
  X, User, Users, Mail, Phone, MessageSquare, PhoneCall, History, Clock,
  AlertTriangle, ChevronLeft, ChevronRight, CalendarDays, Download, ScrollText,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { useClientGroups } from '../../../hooks/useClientGroups.js'
import ClientConversations from '../../../components/agent/clients/ClientConversations.js'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import type { ClientRecord } from '../../../hooks/useClients.js'
import { formatDate, formatDuration } from '../../../utils/formatDate.js'
import { formatClockTime, formatDay } from '../../../utils/timezone.js'
import { useToast } from '../../system/ToastProvider.js'
import Select from '../../ui/Select.js'
import DatePicker from '../../ui/DatePicker.js'

export type ClientViewDrawerTab = 'basicInfo' | 'conversations' | 'contactHistory' | 'auditLog'

export interface ClientViewDrawerProps {
  open: boolean
  onClose: () => void
  clientId: string
  initialTab?: ClientViewDrawerTab
}

/** One row of the merged calls + chats timeline, as returned by the API. */
interface HistoryItem {
  id: string
  type: 'call' | 'chat'
  reference: string | null
  detail: string
  channel: string
  at: string
  status: string | null
  durationSec: number | null
  handledBy: string
}

interface ContactHistoryResponse {
  summary: {
    totalCalls: number
    totalChats: number
    agentsHandled: number
    totalInteractionSec: number
  }
  items: HistoryItem[]
  total: number
  page: number
  limit: number
}

interface AuditLogRow {
  id: string
  actorId: string | null
  actorName: string | null
  action: string
  resourceType: string | null
  resourceId: string | null
  payload: Record<string, unknown> | null
  createdAt: string
}

interface AuditLogResponse {
  rows: AuditLogRow[]
  total: number
  limit: number
  offset: number
}

type HistoryTypeFilter = 'all' | 'chat' | 'call'

const HISTORY_PAGE_SIZE = 10
const AUDIT_PAGE_SIZE = 25
/** The API caps `limit` at 100, so Export pages through in 100-row batches. */
const EXPORT_PAGE_SIZE = 10000
const EXPORT_MAX_PAGES = 5000

const AVATAR_COLORS = [
  'bg-[#3366FF]', 'bg-emerald-500', 'bg-rose-500', 'bg-amber-500', 'bg-cyan-500',
  'bg-violet-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500', 'bg-indigo-500',
]

function getInitials(name: string | null, fallback: string): string {
  if (name) {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
    if (parts.length === 1) return (parts[0] ?? fallback).slice(0, 2).toUpperCase()
  }
  return fallback.slice(0, 2).toUpperCase()
}

function getAvatarColor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? 'bg-[#3366FF]'
}

function StatTile({ icon, iconBg, iconColor, value, label, sublabel }: {
  icon: JSX.Element
  iconBg: string
  iconColor: string
  value: string
  label: string
  sublabel: string
}): JSX.Element {
  return (
    <div className="flex flex-1 items-center gap-3 rounded-[3px] border border-slate-200 bg-white px-4 py-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: iconBg, color: iconColor }}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold leading-tight text-[#0f172a]">{value}</p>
        <p className="truncate text-xs font-medium text-slate-500">{label}</p>
        <p className="truncate text-[11px] text-slate-400">{sublabel}</p>
      </div>
    </div>
  )
}

function HistoryStatusBadge({ status }: { status: string }): JSX.Element {
  const normalized = status.toLowerCase()
  const tone = ['completed', 'closed', 'resolved', 'answered'].includes(normalized)
    ? 'bg-emerald-50 text-emerald-700'
    : ['open', 'reopen', 'in_progress', 'active'].includes(normalized)
      ? 'bg-blue-50 text-blue-700'
      : ['on_hold', 'pending'].includes(normalized)
        ? 'bg-amber-50 text-amber-700'
        : ['failed', 'abandoned', 'no_answer', 'busy'].includes(normalized)
          ? 'bg-red-50 text-red-600'
          : 'bg-slate-100 text-slate-600'
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${tone}`}>{label}</span>
}

/** `Ticket #TKT-1` / `Call ID #abc` — the pill under the request details. */
function referenceLabel(item: HistoryItem): string | null {
  if (!item.reference) return null
  return item.type === 'chat' ? `Ticket #${item.reference}` : `Call ID #${item.reference}`
}

/**
 * Channel-aware request label: "WhatsApp Chat" / "Facebook Chat" / "Live Chat"
 * for chat conversations, "Voice Call" for calls — instead of a generic
 * "Chat Request"/"Call Request" for every channel.
 */
function requestLabel(item: HistoryItem): string {
  // 'voice' is always a call, even if the row's type is 'chat' (a call that
  // spawned a conversation record) — "Voice Chat" reads wrongly.
  if (item.channel === 'voice') return 'Voice Call'
  if (item.type === 'call') return `${getChannelConfig(item.channel).label} Call`
  // 'chat'/'web' is the live-chat channel on the website.
  if (item.channel === 'chat' || item.channel === 'web') return 'Web'
  // 'email' is already a complete label — "Email Chat" reads oddly.
  if (item.channel === 'email') return 'Email'
  return `${getChannelConfig(item.channel).label} Chat`
}

function exportHistoryCsv(items: HistoryItem[], clientName: string): void {
  const header = ['Request Type', 'Request Details', 'Reference', 'Date', 'Time', 'Handled By', 'Status', 'Duration']
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`
  const lines = items.map((r) => [
    requestLabel(r),
    r.detail,
    referenceLabel(r) ?? '',
    formatDay(r.at),
    formatClockTime(r.at),
    r.handledBy,
    r.status ?? '',
    r.durationSec != null ? formatDuration(r.durationSec * 1000) : '',
  ].map(escape).join(','))
  const csv = [header.map(escape).join(','), ...lines].join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `${clientName.replace(/\s+/g, '-').toLowerCase()}-interaction-history.csv`
  link.click()
  URL.revokeObjectURL(url)
}

function formatInteractionTime(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return '0m'
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

function formatClientInfoValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function InfoRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-[3px] border border-slate-100 bg-white p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-0.5 break-words text-sm font-medium text-[#0f172a]">{value}</p>
    </div>
  )
}

/**
 * Client drawer: the client's interaction history (calls + chat requests).
 * in CreateClientPanel, so this drawer stays focused on read-only history.
 */
export default function ClientViewDrawer({ open, onClose, clientId, initialTab = 'basicInfo' }: ClientViewDrawerProps): JSX.Element | null {
  const toast = useToast()
  const navigate = useNavigate()
  const { groups } = useClientGroups(open)
  const [client, setClient] = useState<ClientRecord | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<ClientViewDrawerTab>(initialTab)
  const [error, setError] = useState<string | null>(null)
  const [contactHistory, setContactHistory] = useState<ContactHistoryResponse | null>(null)
  const [loadingContactHistory, setLoadingContactHistory] = useState(false)
  const [historyTypeFilter, setHistoryTypeFilter] = useState<HistoryTypeFilter>('all')
  const [historyPage, setHistoryPage] = useState(1)
  const [historyFrom, setHistoryFrom] = useState('')
  const [historyTo, setHistoryTo] = useState('')
  const [exporting, setExporting] = useState(false)
  const [auditLog, setAuditLog] = useState<AuditLogResponse | null>(null)
  const [loadingAuditLog, setLoadingAuditLog] = useState(false)
  const [auditPage, setAuditPage] = useState(1)
  const [auditActionFilter, setAuditActionFilter] = useState('')
  const [auditResourceTypeFilter, setAuditResourceTypeFilter] = useState('')

  useEffect(() => {
    if (!open || !clientId) return
    setLoading(true)
    setError(null)
    api.get<{ client: ClientRecord }>(`/admin/clients/${clientId}`)
      .then((res) => setClient(res.client))
      .catch((e) => {
        const message = e instanceof Error ? e.message : 'Unknown error occurred'
        setError(`Failed to load client data: ${message}`)
        toast.error('Failed to Load Client', message)
      })
      .finally(() => setLoading(false))
  }, [open, clientId])

  useEffect(() => {
    if (!open) {
      setClient(null)
      setError(null)
      setContactHistory(null)
      setHistoryTypeFilter('all')
      setHistoryPage(1)
      setHistoryFrom('')
      setHistoryTo('')
      setAuditLog(null)
      setAuditPage(1)
      setAuditActionFilter('')
      setAuditResourceTypeFilter('')
    }
  }, [open])

  useEffect(() => {
    if (open) setActiveTab(initialTab)
  }, [open, initialTab])

  // Calls + chat requests recorded against this client. Filtering, sorting and
  // pagination are the API's job, so every filter change refetches rather than
  // slicing a client-side copy — a client with years of history never has to
  // be shipped to the browser in full.
  useEffect(() => {
    if (!open || activeTab !== 'contactHistory' || !clientId) return
    const params = new URLSearchParams({
      type: historyTypeFilter,
      page: String(historyPage),
      limit: String(HISTORY_PAGE_SIZE),
    })
    if (historyFrom) params.set('from', historyFrom)
    if (historyTo) params.set('to', historyTo)

    let cancelled = false
    setLoadingContactHistory(true)
    api.get<ContactHistoryResponse>(`/agent/clients/${clientId}/contact-history?${params.toString()}`)
      .then((res) => { if (!cancelled) setContactHistory(res) })
      .catch(() => { if (!cancelled) setContactHistory(null) })
      .finally(() => { if (!cancelled) setLoadingContactHistory(false) })
    return () => { cancelled = true }
  }, [open, activeTab, clientId, historyTypeFilter, historyPage, historyFrom, historyTo])

  // Audit log entries related to this client — fetched when the Audit Log
  // tab is active. Paginated server-side; each filter/page change refetches.
  useEffect(() => {
    if (!open || activeTab !== 'auditLog' || !clientId) return
    let cancelled = false
    setLoadingAuditLog(true)
    const params = new URLSearchParams({
      limit: String(AUDIT_PAGE_SIZE),
      offset: String((auditPage - 1) * AUDIT_PAGE_SIZE),
    })
    if (auditActionFilter.trim()) params.set('action', auditActionFilter.trim())
    if (auditResourceTypeFilter.trim()) params.set('resourceType', auditResourceTypeFilter.trim())
    api.get<AuditLogResponse>(`/agent/clients/${clientId}/audit-log?${params.toString()}`)
      .then((res) => { if (!cancelled) setAuditLog(res) })
      .catch(() => { if (!cancelled) setAuditLog(null) })
      .finally(() => { if (!cancelled) setLoadingAuditLog(false) })
    return () => { cancelled = true }
  }, [open, activeTab, clientId, auditPage, auditActionFilter, auditResourceTypeFilter])

  /**
   * Export the whole filtered result set, not just the page on screen — so it
   * pages through the API (which caps `limit` at EXPORT_PAGE_SIZE) instead of
   * reusing the rows already rendered.
   */
  const handleExport = async (): Promise<void> => {
    if (!clientId) return
    setExporting(true)
    try {
      const collected: HistoryItem[] = []
      for (let p = 1; p <= EXPORT_MAX_PAGES; p++) {
        const params = new URLSearchParams({
          type: historyTypeFilter,
          page: String(p),
          limit: String(EXPORT_PAGE_SIZE),
        })
        if (historyFrom) params.set('from', historyFrom)
        if (historyTo) params.set('to', historyTo)
        const res = await api.get<ContactHistoryResponse>(`/agent/clients/${clientId}/contact-history?${params.toString()}`)
        collected.push(...res.items)
        if (collected.length >= res.total || res.items.length === 0) break
      }
      exportHistoryCsv(collected, client?.full_name || client?.email || 'client')
    } catch (e) {
      toast.error('Export Failed', e instanceof Error ? e.message : 'Could not export interaction history')
    } finally {
      setExporting(false)
    }
  }

  if (!open) return null

  const groupName = client?.group_id ? groups.find((g) => g.id === client.group_id)?.name : null
  const cardClass = 'rounded-[3px] border border-slate-200 bg-white p-5 shadow-sm'

  const summary = contactHistory?.summary
  const pageRows = contactHistory?.items ?? []
  const total = contactHistory?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE))
  const page = Math.min(historyPage, totalPages)
  const start = (page - 1) * HISTORY_PAGE_SIZE
  const hasFilters = historyTypeFilter !== 'all' || historyFrom !== '' || historyTo !== ''

  const clearHistoryFilters = () => {
    setHistoryTypeFilter('all')
    setHistoryFrom('')
    setHistoryTo('')
    setHistoryPage(1)
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 flex h-full w-[80%] flex-col bg-slate-50 shadow-2xl transition-transform">
        {/* Header — client identity on the left, at-a-glance tiles on the right */}
        <div className="relative flex flex-wrap items-center justify-between gap-6 border-b border-slate-200 bg-white px-8 py-5 pr-16">
          <div className="flex min-w-[16rem] items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#EDE9FE] text-lg font-bold text-[#6D28D9]">
              {client ? getInitials(client.full_name, client.email) : '?'}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <h2 className="truncate text-lg font-bold text-[#0f172a]">{client ? client.full_name || 'N/A' : 'Loading…'}</h2>
                {client && (
                  <span className={`inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${client.status ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                    {client.status ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>
              <div className="mt-1.5 space-y-1">
                <p className="flex items-center gap-2 text-xs text-slate-600">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="truncate">{client?.email || '—'}</span>
                </p>
                <p className="flex items-center gap-2 text-xs text-slate-600">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="truncate">{client?.phone_number || '—'}</span>
                </p>
                <p className="flex items-center gap-2 text-xs text-slate-600">
                  <Users className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="truncate">{groupName || 'No Group'}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-1 flex-wrap items-stretch gap-4">
            <StatTile
              icon={<MessageSquare className="h-5 w-5" />} iconBg="#EFF4FF" iconColor="#3366FF"
              value={String(summary?.totalChats ?? 0)} label="Chat Requests" sublabel="(All Time)"
            />
            <StatTile
              icon={<PhoneCall className="h-5 w-5" />} iconBg="#ECFDF5" iconColor="#059669"
              value={String(summary?.totalCalls ?? 0)} label="Call Requests" sublabel="(All Time)"
            />
            <StatTile
              icon={<Users className="h-5 w-5" />} iconBg="#F5F3FF" iconColor="#7C3AED"
              value={String(summary?.agentsHandled ?? 0)} label="Requests Handled" sublabel="by Agents"
            />
            <StatTile
              icon={<Clock className="h-5 w-5" />} iconBg="#FFFBEB" iconColor="#D97706"
              value={formatInteractionTime(summary?.totalInteractionSec ?? 0)} label="Total Interaction" sublabel="Time"
            />
          </div>

          <div className="absolute right-5 top-5 flex items-center gap-2">
            <Link
              to={`/agent/customers/${clientId}`}
              className="flex h-9 items-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-3 text-[12.5px] font-semibold text-slate-600 transition-colors hover:text-slate-900"
              title="Open the consolidated cross-channel profile"
            >
              <History className="h-3.5 w-3.5" /> Customer 360
            </Link>
            <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-[3px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs — segmented-pill style to match the conversation inbox tabs */}
        <div className="flex border-b border-slate-200 bg-white px-8 py-3">
          <div className="flex rounded-[3px] bg-slate-100 p-1">
            {[
              { key: 'basicInfo', label: 'Basic Info', icon: User },
              { key: 'conversations', label: 'Conversations', icon: MessageSquare },
              { key: 'contactHistory', label: 'History', icon: History },
              { key: 'auditLog', label: 'Audit Log', icon: ScrollText },
            ].map((t) => {
              const Icon = t.icon
              const on = activeTab === t.key
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key as ClientViewDrawerTab)}
                  className={`flex items-center gap-2 rounded-[3px] px-3.5 py-1.5 text-[13px] font-semibold transition-all ${
                    on
                      ? 'bg-white text-[#0f172a] shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <span className={on ? 'text-[#3366FF]' : 'text-slate-400'}>
                    <Icon className="h-4 w-4" />
                  </span>
                  {t.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {loading && <div className="py-10 text-center text-sm text-slate-400">Loading…</div>}

          {!loading && error && (
            <div className="rounded-[3px] border border-red-200 bg-red-50 p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-900">Failed to Load Client Data</h3>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && activeTab === 'basicInfo' && client && (
            <div className="space-y-4">
              <div className={cardClass}>
                <h3 className="mb-4 text-base font-bold text-[#0f172a]">Client Basic Information</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <InfoRow label="Client ID" value={client.id} />
                  <InfoRow label="Full Name" value={client.full_name ?? '—'} />
                  <InfoRow label="Email" value={client.email} />
                  <InfoRow label="Phone Number" value={client.phone_number ?? '—'} />
                  <InfoRow label="Status" value={client.status ? 'Active' : 'Inactive'} />
                  <InfoRow label="Account" value={client.locked ? 'Locked' : 'Unlocked'} />
                  <InfoRow label="Client Group" value={groupName ?? 'No Group'} />
                  <InfoRow label="Created At" value={formatDate(client.createdAt)} />
                  <InfoRow label="Updated At" value={formatDate(client.updatedAt)} />
                </div>
              </div>

              {Object.keys(client.additionalFields ?? {}).length > 0 && (
                <div className={cardClass}>
                  <h3 className="mb-4 text-base font-bold text-[#0f172a]">Additional Fields</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {Object.entries(client.additionalFields).map(([key, value]) => (
                      <InfoRow key={key} label={key} value={formatClientInfoValue(value)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && !error && activeTab === 'conversations' && client && <ClientConversations clientId={client.id} />}

          {!loading && !error && activeTab === 'contactHistory' && (
            loadingContactHistory ? (
              <div className="py-10 text-center text-sm text-slate-400">Loading interaction history…</div>
            ) : total === 0 && !hasFilters ? (
              <div className={cardClass}>
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <History className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">No History</h3>
                  <p className="mt-1 text-xs text-slate-500">No calls or chat requests have been recorded for this client.</p>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-[3px] border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div>
                    <h3 className="text-base font-bold text-[#0f172a]">Client Interaction History</h3>
                    <p className="mt-0.5 text-xs text-slate-500">Complete history of chat and call requests for this client.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <DatePicker
                      ariaLabel="History from"
                      size="sm"
                      label="From"
                      value={historyFrom}
                      onChange={(v) => { setHistoryFrom(v); setHistoryPage(1) }}
                    />
                    <DatePicker
                      ariaLabel="History to"
                      size="sm"
                      label="To"
                      value={historyTo}
                      onChange={(v) => { setHistoryTo(v); setHistoryPage(1) }}
                    />
                    <Select
                      ariaLabel="Request type"
                      value={historyTypeFilter}
                      onChange={(v) => { setHistoryTypeFilter(v as HistoryTypeFilter); setHistoryPage(1) }}
                    >
                      <option value="all">All Request Types</option>
                      <option value="chat">Chat Requests</option>
                      <option value="call">Call Requests</option>
                    </Select>
                    {hasFilters && (
                      <button
                        onClick={clearHistoryFilters}
                        className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <X className="h-4 w-4" />
                        Clear Filters
                      </button>
                    )}
                    {false && (
                      <button
                        onClick={handleExport}
                        disabled={total === 0 || exporting}
                        className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Download className="h-4 w-4" />
                        {exporting ? 'Exporting…' : 'Export'}
                      </button>
                    )}
                  </div>
                </div>

                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-y border-slate-200 bg-[#F7F8FA]">
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Request Type</th>
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Request Details</th>
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Time</th>
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Handled By</th>
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Status</th>
                      <th className="w-10 px-2 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0 && (
                      <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">No requests match this filter</td></tr>
                    )}
                    {pageRows.map((row, index) => {
                      const ChannelIcon = getChannelConfig(row.channel).icon
                      return (
                      <tr
                        key={`${row.type}-${row.id}`}
                        className={`group border-b border-slate-100 transition-colors last:border-0 ${row.type === 'chat' ? 'cursor-pointer' : ''} hover:bg-[#F7F9FC]`}
                        onClick={row.type === 'chat' ? () => navigate(`/agent/conversation/view/${row.id}`, { state: { tabLabel: row.reference ? `#${row.reference}` : row.id } }) : undefined}
                      >
                        <td className="relative py-5 pl-5 pr-5">
                          {/* Timeline rail linking every request in the list */}
                          <span
                            className={`absolute left-[2.06rem] w-px bg-emerald-100 ${
                              index === 0 ? 'bottom-0 top-1/2' : index === pageRows.length - 1 ? 'bottom-1/2 top-0' : 'inset-y-0'
                            }`}
                          />
                          <div className="relative flex items-center gap-3">
                            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-sm ${row.type === 'chat' ? 'bg-[#3366FF]' : 'bg-emerald-500'}`}>
                              {row.type === 'chat' ? <ChannelIcon className="h-4 w-4" /> : <PhoneCall className="h-4 w-4" />}
                            </div>
                            <span className="text-sm font-semibold text-[#0f172a]">
                              {requestLabel(row)}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-5">
                          <p className="truncate text-sm text-slate-700" title={row.detail}>
                            <span className="text-slate-400">Query: </span>{row.detail}
                          </p>
                          {referenceLabel(row) && (
                            <span className={`mt-1.5 inline-flex rounded-[3px] px-2 py-1 text-[11px] font-semibold ${row.type === 'chat' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>
                              {referenceLabel(row)}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-5">
                          <p className="flex items-center gap-1.5 text-sm text-slate-700">
                            <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            {formatDay(row.at)}
                          </p>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                            <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            {formatClockTime(row.at)}
                          </p>
                        </td>
                        <td className="px-5 py-5">
                          <div className="flex items-center gap-2.5">
                            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${getAvatarColor(row.handledBy)}`}>
                              {getInitials(row.handledBy, row.handledBy)}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-[#0f172a]">{row.handledBy}</p>
                              <p className="text-[11px] text-slate-400">Agent</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-5">
                          <HistoryStatusBadge status={row.status ?? 'unknown'} />
                          <p className="mt-1.5 text-[11px] text-slate-400">
                            Duration: {row.durationSec != null ? formatDuration(row.durationSec * 1000) : '—'}
                          </p>
                        </td>
                        <td className="px-2 py-5 text-right">
                          <ChevronRight className="ml-auto h-4 w-4 text-slate-300 transition-colors group-hover:text-slate-500" />
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>

                {total > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                    <p className="text-xs text-slate-500">
                      Showing {start + 1} to {Math.min(start + HISTORY_PAGE_SIZE, total)} of {total} entries
                    </p>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setHistoryPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="flex h-8 w-8 items-center justify-center rounded-[3px] border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setHistoryPage(i + 1)}
                          className={`h-8 min-w-[2rem] rounded-[3px] border px-2 text-xs font-semibold transition-colors ${
                            page === i + 1
                              ? 'border-[#3366FF] bg-[#3366FF] text-white'
                              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setHistoryPage(Math.min(totalPages, page + 1))}
                        disabled={page >= totalPages}
                        className="flex h-8 w-8 items-center justify-center rounded-[3px] border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          {!loading && !error && activeTab === 'auditLog' && (
            loadingAuditLog ? (
              <div className="py-10 text-center text-sm text-slate-400">Loading audit log…</div>
            ) : (auditLog?.rows.length ?? 0) === 0 ? (
              <div className={cardClass}>
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <ScrollText className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">No Audit Log Entries</h3>
                  <p className="mt-1 text-xs text-slate-500">No audit log entries have been recorded for this client yet.</p>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-[3px] border border-slate-200 bg-white">
                <div className="px-5 py-4">
                  <h3 className="text-base font-bold text-[#0f172a]">Audit Log</h3>
                  <p className="mt-0.5 text-xs text-slate-500">Tamper-evident record of all actions related to this client.</p>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <label htmlFor="audit-action" className="text-xs font-medium text-slate-600">Action</label>
                      <input
                        id="audit-action"
                        type="text"
                        value={auditActionFilter}
                        onChange={(e) => { setAuditPage(1); setAuditActionFilter(e.target.value) }}
                        placeholder="e.g. client.created"
                        className="h-8 w-40 rounded-[3px] border border-slate-200 bg-white px-2.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-[#3366FF] focus:outline-none"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label htmlFor="audit-resource" className="text-xs font-medium text-slate-600">Resource</label>
                      <Select
                        ariaLabel="Audit resource type"
                        size="sm"
                        className="w-36"
                        value={auditResourceTypeFilter}
                        onChange={(v) => { setAuditPage(1); setAuditResourceTypeFilter(v) }}
                      >
                        <option value="">All</option>
                        <option value="client">Client</option>
                        <option value="conversation">Conversation</option>
                        <option value="call">Call</option>
                        <option value="chat">Chat</option>
                        <option value="live_chat">Live Chat</option>
                        <option value="whatsapp_chat">WhatsApp</option>
                        <option value="facebook_chat">Facebook</option>
                        <option value="instagram_chat">Instagram</option>
                      </Select>
                    </div>
                  </div>
                </div>

                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-y border-slate-200 bg-[#F7F8FA]">
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Action</th>
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Resource</th>
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Actor</th>
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Details</th>
                      <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(auditLog?.rows ?? []).map((row) => (
                      <tr key={row.id} className="border-b border-slate-100 transition-colors last:border-0 hover:bg-[#F7F9FC]">
                        <td className="px-5 py-4">
                          <span className="inline-flex rounded-[3px] bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-[#3366FF]">
                            {row.action}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-medium text-[#0f172a]">{row.resourceType ?? '—'}</p>
                          {row.resourceId && (
                            <p className="mt-0.5 truncate text-[11px] text-slate-400" title={row.resourceId}>
                              {row.resourceId.length > 20 ? `${row.resourceId.slice(0, 8)}…${row.resourceId.slice(-8)}` : row.resourceId}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${getAvatarColor(row.actorName ?? row.actorId ?? 'system')}`}>
                              {getInitials(row.actorName, row.actorId ?? 'system')}
                            </div>
                            <span className="truncate text-sm font-medium text-[#0f172a]">{row.actorName ?? 'System'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          {row.payload && Object.keys(row.payload).length > 0 ? (
                            <div className="max-w-xs space-y-0.5">
                              {Object.entries(row.payload).slice(0, 4).map(([key, value]) => (
                                <p key={key} className="truncate text-[11px] text-slate-500" title={`${key}: ${formatClientInfoValue(value)}`}>
                                  <span className="font-semibold text-slate-600">{key}:</span>{' '}
                                  <span className="text-slate-500">{formatClientInfoValue(value)}</span>
                                </p>
                              ))}
                              {Object.keys(row.payload).length > 4 && (
                                <p className="text-[11px] text-slate-400">+{Object.keys(row.payload).length - 4} more</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <p className="flex items-center gap-1.5 text-sm text-slate-700">
                            <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            {formatDay(row.createdAt)}
                          </p>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                            <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            {formatClockTime(row.createdAt)}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {(auditLog?.total ?? 0) > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                    <p className="text-xs text-slate-500">
                      Showing {((auditPage - 1) * AUDIT_PAGE_SIZE) + 1} to {Math.min(auditPage * AUDIT_PAGE_SIZE, auditLog?.total ?? 0)} of {auditLog?.total ?? 0} entries
                    </p>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setAuditPage(Math.max(1, auditPage - 1))}
                        disabled={auditPage === 1}
                        className="flex h-8 w-8 items-center justify-center rounded-[3px] border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      {Array.from({ length: Math.min(10, Math.ceil((auditLog?.total ?? 0) / AUDIT_PAGE_SIZE)) }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setAuditPage(i + 1)}
                          className={`h-8 min-w-[2rem] rounded-[3px] border px-2 text-xs font-semibold transition-colors ${
                            auditPage === i + 1
                              ? 'border-[#3366FF] bg-[#3366FF] text-white'
                              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setAuditPage(Math.min(Math.ceil((auditLog?.total ?? 0) / AUDIT_PAGE_SIZE), auditPage + 1))}
                        disabled={auditPage >= Math.ceil((auditLog?.total ?? 0) / AUDIT_PAGE_SIZE)}
                        className="flex h-8 w-8 items-center justify-center rounded-[3px] border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          )}


        </div>
      </div>
    </>
  )
}
